import { Component, h } from 'preact'
import tracker from '../../components/tracker/publish'
import TopicHelpfulness from './topicHelpfulness'
import Portal from 'preact-portal'
import axios from 'axios'
import getTopicType from '../../utils/getTopicType'
import { CONTENT_HELPFULNESS_VOTE_URL, CONTENT_HELPFULNESS_SETTINGS_URL, ratingDataMap } from '../../libs/config'
import Analytics from '../analytics/Analytics'

type Boundaries = {
  firstPost: number,
  replyBoxTop: boolean,
  replyBoxBottom: boolean,
  hasReplyBox: boolean
}

type TopicHelpfulnessType = 'topichelpful' | 'topicneutral' | 'topicnothelpful'

interface Props {
  phrases: {
    Forum: Object
  },
  topic: {
    contentType: string | null,
    id: string,
    prefixId: string,
    title: string,
    views: number
  }
}

interface State {
  value: TopicHelpfulnessType | null,
  isDismissed: boolean,
  isRemoved: boolean,
  isSticky: boolean,
  animationClass: string | null,
  visible: boolean,
  indicatorEnabled: boolean,
  helpfulnessPercentage: null,
  configurationLoaded: boolean
}

class TopicHelpfulnessContainer extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      value: null,
      isDismissed: false,
      isRemoved: false,
      isSticky: false,
      animationClass: null,
      visible: false,
      indicatorEnabled: false,
      helpfulnessPercentage: null,
      configurationLoaded: false
    }
    this.setData = this.setData.bind(this)
    this.hideWidget = this.hideWidget.bind(this)
    this.onScroll = this.onScroll.bind(this)
    this.oldScroll = window.pageYOffset || document.documentElement.scrollTop
  }

  oldScroll: number

  updateStorage () {
    let data = JSON.parse(window.localStorage.getItem('topicHelpfulness') || '{}')
    data[this.props.topic.id] = { value: this.state.value, isDismissed: this.state.isDismissed, isRemoved: this.state.isRemoved }
    window.localStorage.setItem('topicHelpfulness', JSON.stringify(data))
  }

  removeWidget () {
    this.setState({ isRemoved: true }, () => this.updateStorage())
  }

  hideWidget () {
    window.removeEventListener('scroll', this.onScroll)
    this.setState({ animationClass: 'easy-out' })
    window.setTimeout(() => {
      this.setState({ isDismissed: true, isSticky: false, animationClass: '' })
    }, 300)
  }

  async setData (value: TopicHelpfulnessType) {
    this.setState({ animationClass: 'fade-out' })

    window.setTimeout(() => {
      this.setState({ value: value, animationClass: '' })
      window.setTimeout(() => {
        this.hideWidget()
      }, 3000)
    }, 300)

    Analytics.send('Topic helpfulness', ratingDataMap.ga[value])

    tracker.trackEvent('userevent', {
      type: 'Topic Rated',
      rating: ratingDataMap.messagebus[value],
      topic_title: this.props.topic.title,
      topic_type: getTopicType(this.props.topic.prefixId, this.props.topic.contentType),
      topic_id: this.props.topic.id,
      topic_url: window.location.origin + window.location.pathname,
      topic_views: this.props.topic.views,
      source: 'community'
    })

    await axios.post(CONTENT_HELPFULNESS_VOTE_URL, {
      topic: this.props.topic.id,
      vote: value
    })
  }

  componentDidMount () {
    const data = JSON.parse(window.localStorage.getItem('topicHelpfulness') || '{}')
    if (data[this.props.topic.id]) {
      const { value, isDismissed, isRemoved } = data[this.props.topic.id]
      if (!isRemoved) {
        this.getConfiguration()
      }
      this.setState({ value, isDismissed, isRemoved })
    } else {
      this.getConfiguration()
    }

    window.addEventListener('beforeunload', () => {
      if (this.state.value) {
        this.removeWidget()
      }
    })

    if (!this.state.isDismissed) {
      const boundaries = this.getScrollBoundaries()

      if (!boundaries) {
        return
      }
      if (boundaries.firstPost < 0 && (!boundaries.hasReplyBox || boundaries.replyBoxTop)) {
        this.setState({ isSticky: true })
      }
      window.addEventListener('scroll', this.onScroll)
    }
  }

  onScroll () {
    const isSticky = this.state.isSticky
    const boundaries = this.getScrollBoundaries()

    const newScroll = window.pageYOffset || document.documentElement.scrollTop

    if (newScroll < this.oldScroll) {
      this.oldScroll = newScroll
    } else {
      this.setState({ isSticky: false })
      this.oldScroll = newScroll
      return
    }

    if (!boundaries) {
      return
    }

    if (!isSticky && boundaries.firstPost < 0 && (!boundaries.hasReplyBox || boundaries.replyBoxTop)) {
      this.setState({ isSticky: true })
    } else if (isSticky && (boundaries.firstPost > 0 || (boundaries.hasReplyBox && boundaries.replyBoxBottom))) {
      this.setState({ isSticky: false })
    }
  }

  getScrollBoundaries (): Boundaries | null {
    const firstPostElement = document.querySelector('.qa-topic-first-post')
    const firstPost: DOMRect | null = firstPostElement && firstPostElement.getBoundingClientRect()
    const hasReplyBox: boolean = document.querySelector('.qa-reply') !== null
    const replyBoxElement = document.querySelector('.qa-reply')
    const replyBox: DOMRect | null = (hasReplyBox && replyBoxElement) ? replyBoxElement.getBoundingClientRect() : null

    if (!firstPost || !replyBox) {
      return null
    }

    return {
      firstPost: firstPost.height + firstPost.top,
      replyBoxTop: replyBox.top > window.innerHeight,
      replyBoxBottom: replyBox.top < window.innerHeight,
      hasReplyBox: hasReplyBox
    }
  }

  async getConfiguration () {
    if (this.state.configurationLoaded) {
      return
    }

    const { data } = await axios.get(CONTENT_HELPFULNESS_SETTINGS_URL, {
      params: {
        topicId: this.props.topic.id,
        topicType: getTopicType(this.props.topic.prefixId, this.props.topic.contentType)
      }
    })

    this.setState({
      visible: data.visible,
      indicatorEnabled: data.indicatorEnabled,
      helpfulnessPercentage: data.helpfulnessPercentage,
      configurationLoaded: true
    })
  }

  componentDidUpdate () {
    this.updateStorage()
  }

  render ({ phrases }: Props, { animationClass, isRemoved, value, isDismissed, isSticky,
    visible, indicatorEnabled, helpfulnessPercentage }: State) {
    const isVoted = value
    if (isRemoved || (isDismissed && isSticky) || !visible) {
      return null
    }
    const topicHelpfulness = <TopicHelpfulness
      setData={this.setData}
      hideWidget={this.hideWidget}
      isVoted={isVoted}
      isSticky={isSticky}
      phrases={phrases.Forum}
      showIndicator={indicatorEnabled}
      indicatorPercentage={helpfulnessPercentage}
      animationClass={animationClass}
    />

    return (
      <div>
        {topicHelpfulness}
        {isSticky ? (<Portal into={'#comments'}>{topicHelpfulness}</Portal>) : null}
      </div>
    )
  }
}

export default TopicHelpfulnessContainer
